import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Formik, Field, Form } from 'formik';
import { isMobile } from 'react-device-detect';
import ButtonFilter from '../../../components/Table/Filters/ButtonFilter';
import { TableContext } from 'Components/Table/TableContext';
import Calendar from 'Components/Calendar/Calendar';
import { useQuery } from '@apollo/react-hooks';
import { UPCOMING_VENUE_EVENTS } from 'Queries/Admin/UpcomingVenueEvents';
import { Select } from 'formik-material-ui';
import { MenuItem } from '@material-ui/core';

const DashboardFilersBase = props => {
  useContext(TableContext);
  const { open, setFilters, setLoadingEvents, setErrorEvents } = props;
  const [initialValues, setInitialValues] = useState({
    eventId: '',
    startDate: '',
    endDate: ''
  });

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [eventId, setEventId] = useState('');
  const [selectedEventStartDate, setSelectedEventStartDate] = useState('');
  const [selectedEventEndDate, setSelectedEventEndDate] = useState('');
  const [events, setEvents] = useState([]);

  const { loading: loadingEvents, error: errorEvents, data: venueEvents } = useQuery(UPCOMING_VENUE_EVENTS, {
    variables: {
      filterBy: {
        // Get events that haven't ended more than 30 days ago
        endDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }
    },
    fetchPolicy: 'network-only'
  });

  // Process obtained events
  useEffect(() => {
    setLoadingEvents(loadingEvents);
    setErrorEvents(errorEvents);
    if (venueEvents?.venue?.events) {
      // Sort events by ID (newest to oldest)
      const sortedEvents = [...venueEvents.venue.events].sort((a, b) => parseInt(b.id) - parseInt(a.id));
      setEvents(sortedEvents);
    }
  }, [venueEvents, loadingEvents, errorEvents]);

  const handleDateRange = ({ startDate, endDate }) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleEventChange = eventId => {
    setEventId(eventId);
    // Clear dates when event changes to force user to select new dates
    setStartDate('');
    setEndDate('');
    // Also clear initialValues to ensure Formik is synchronized
    setInitialValues(prev => ({
      ...prev,
      eventId: eventId,
      startDate: '',
      endDate: ''
    }));
  };

  const handleSubmit = () => {
    const v = {
      eventId: eventId ? eventId : '',
      startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
      endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : ''
    };
    setFilters(v);
  };

  const handleClear = () => {
    const filterValues = {
      eventId: '',
      startDate: '',
      endDate: ''
    };

    // Clear all state
    setStartDate('');
    setEndDate('');
    setEventId('');
    setSelectedEventStartDate('');
    setSelectedEventEndDate('');
    setFilters(filterValues);
    setInitialValues(filterValues);
  };

  useEffect(() => {
    if (eventId) {
      const event = events.find(e => e.id === eventId);
      if (event) {
        setSelectedEventStartDate(event.startDate);
        setSelectedEventEndDate(event.endDate);
      }
      // Update initialValues to reflect the selected event
      setInitialValues(prev => ({
        ...prev,
        eventId: eventId
      }));
    } else {
      // Clear the selected event dates when no event is selected
      setSelectedEventStartDate('');
      setSelectedEventEndDate('');
    }
  }, [eventId, events]);

  const options = events.map(e => (
    <MenuItem key={e.id} value={e.id}>
      {e.name}
    </MenuItem>
  ));

  return (
    <div isMobile={isMobile} className={`${isMobile && !open ? 'closed' : ''}`}>
      <DashboardFilters>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
          {(
            { values, resetForm } // eslint-disable-line
          ) => (
            <Form className="filters-form">
              <div className="input-group">
                <Field name="eventId">
                  {({ meta, ...props }) => (
                    <Select value={eventId} {...props} meta={meta} disabled={false} onChange={e => handleEventChange(e.target.value)}>
                      {options}
                    </Select>
                  )}
                </Field>
                <Calendar
                  selectableRangeStart={selectedEventStartDate}
                  startDatePlaceholder={'START'}
                  endDatePlaceholder={'END'}
                  selectableRangeEnd={selectedEventEndDate}
                  plural={true}
                  cb={handleDateRange}
                  disabled={!eventId}
                  key={`calendar-${eventId}-${selectedEventStartDate}-${selectedEventEndDate}`}
                  defaultValue={[null, null]}
                />
              </div>
              <div className="button-group">
                <ButtonFilter type="submit" variant="contained" color="primary" fullWidth disabled={!eventId || !startDate || !endDate}>
                  apply filters
                </ButtonFilter>
                <ButtonFilter
                  color="primary"
                  fullWidth
                  onClick={() => {
                    handleClear();
                    resetForm();
                  }}>
                  clear filters
                </ButtonFilter>
              </div>
            </Form>
          )}
        </Formik>
      </DashboardFilters>
    </div>
  );
};

const DashboardFilters = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  .filters-form {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 30px;
  }

  .input-group,
  .button-group {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .input-group {
    gap: 15px;
    .MuiInputBase-root {
      max-width: 500px;
      min-width: 200px;
    }

    .ant-picker {
      min-width: 250px;
    }
  }

  .button-group {
    gap: 15px;
    button {
      max-width: 200px;
      min-width: 100px;
    }
  }
`;

export default DashboardFilersBase;
